import { Reveal } from "@/components/site/Reveal";
import Polaroid from "@/components/site/Polaroid";
import { IMG } from "@/components/site/content";
import { useLang } from "@/context/LanguageContext";

function Chapter({ n, label, dark }) {
  return (
    <div className="mb-6 flex items-center gap-4">
      <span className={`font-display text-5xl ${dark ? "text-outline-sable" : "text-outline-navy"}`}>
        {n}
      </span>
      <span className={`font-marker text-lg ${dark ? "text-[#EA7A4F]" : "text-[#3F9BA6]"}`}>
        {label}
      </span>
    </div>
  );
}

export default function Story() {
  const { t } = useLang();
  const s1 = t.s1;
  const s2 = t.s2;
  const s3 = t.s3;

  return (
    <>
      {/* 1. Tu connais cette vie */}
      <section className="bg-[#F6F1E8] py-24 sm:py-32" data-testid="section-tu-connais">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-2">
          <div>
            <Chapter n="01" label={s1.kicker} />
            <Reveal>
              <h2 className="font-display text-4xl leading-[0.95] text-[#14283A] sm:text-6xl">
                {s1.title}
              </h2>
            </Reveal>
            {s1.body.map((p, i) => (
              <Reveal key={i} delay={0.08 + i * 0.06}>
                <p className="mt-6 max-w-lg text-lg text-[#14283A]/80">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <p className="mt-4 max-w-lg text-lg font-semibold text-[#14283A]">
                <span className="grad-underline">{s1.punch}</span>
              </p>
            </Reveal>
          </div>
          <div className="flex justify-center gap-6">
            <Polaroid src={IMG.scrap2} alt="" caption={s1.cap1} rotate="-4deg" />
            <Polaroid src={IMG.scrap1} alt="" caption={s1.cap2} rotate="5deg" className="mt-16" delay={0.12} />
          </div>
        </div>
      </section>

      {/* 2. Le concept */}
      <section id="concept" className="bg-[#14283A] py-24 text-[#F6F1E8] sm:py-32" data-testid="section-concept">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal className="order-2 lg:order-1">
            <figure className="relative mx-auto max-w-md rotate-[-3deg] bg-white p-3 pb-9 shadow-2xl">
              <span className="tape -top-3 left-8 rotate-6" aria-hidden="true" />
              <img src={IMG.nightlife} alt="" className="aspect-[5/4] w-full object-cover" />
              <figcaption className="font-marker absolute bottom-2 left-0 right-0 text-center text-[#14283A]">
                {s2.caption}
              </figcaption>
            </figure>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Chapter n="02" label={s2.kicker} dark />
            <Reveal>
              <h2 className="font-display text-3xl leading-[1] sm:text-5xl">
                {s2.titlePre}
                <span className="grad-text">{s2.titleAccent}</span>
                {s2.titlePost}
              </h2>
            </Reveal>
            {s2.body.map((p, i) => (
              <Reveal key={i} delay={0.08 + i * 0.06}>
                <p className="mt-5 max-w-xl text-lg text-[#F6F1E8]/80">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {s2.cards.map(([big, small]) => (
                  <div key={small} className="rounded-xl border border-[#F6F1E8]/20 p-4">
                    <div className="grad-text font-display text-3xl">{big}</div>
                    <div className="text-sm text-[#F6F1E8]/70">{small}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. La communauté SZN */}
      <section id="communaute" className="bg-[#F6F1E8] py-24 sm:py-32" data-testid="section-communaute">
        <div className="mx-auto max-w-7xl px-5">
          <Chapter n="03" label={s3.kicker} />
          <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
            <div>
              <Reveal>
                <h2 className="font-display text-4xl leading-[0.95] text-[#14283A] sm:text-6xl">
                  {s3.titlePre} <span className="grad-underline">{s3.titleAccent}</span> {s3.titlePost}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-lg text-lg text-[#14283A]/80">{s3.body}</p>
              </Reveal>
              <Reveal delay={0.18}>
                <ul className="mt-6 space-y-3">
                  {s3.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-[#14283A]">
                      <span className="grad-bar h-2.5 w-2.5 shrink-0 rounded-full" />
                      <span className="font-medium">{b}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="relative flex justify-center gap-6">
              <Polaroid src={IMG.scrap1} alt="" caption={s3.cap1} rotate="4deg" />
              <Polaroid src={IMG.heroVibe} alt="" caption={s3.cap2} rotate="-5deg" className="mt-20" delay={0.12} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
